import { ReactNode, useRef, useState } from 'react';
import { SubmitHandler, useForm, type UseFormReturn } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';

import Button from '~/shared/components/ButtonsV2';
import DeleteIcon from '~/shared/assets/icons/delete.svg';
import { Input } from '~/shared/components/Input';
import Typography from '~/shared/components/Typography';
import Guard from '~/shared/components/Guard';

import { RemoveRoleArnModal } from './RemoveRoleArnModal';
import { SkipRoleArnValidationModal } from './SkipRoleArnValidationModal';
import {
  getRoleArnErrorState,
  getRoleArnInteractionState,
  useRoleArnMutations,
  type RoleArnInfo,
} from '../useRoleArnMutations';

export type RoleArnVariant = 'SingleAccountView' | 'OrganizationAccountView';

/** State passed from RoleArnRow to `renderFooter` (SingleAccountView only). */
export type RoleArnRowFooterRenderProps = {
  isLoading: boolean;
  isRoleArnLoading: boolean;
  isSubmitting: boolean;
  isUpdatePending: boolean;
  forceEditable: boolean;
  roleArn: string | undefined;
  footerErrorId: string;
  hasRootError: boolean;
};

type RoleArnRowProps = {
  accountId: string;
  accountName?: string;
  isOrganization: boolean;
  variant: RoleArnVariant;
  showDevTools?: boolean;
  /** Used only when `variant` is SingleAccountView. Renders below the role input row (e.g. SectionFooter). */
  renderFooter?: (state: RoleArnRowFooterRenderProps) => ReactNode;
};

export function RoleArnRow({
  accountId,
  accountName,
  isOrganization,
  variant,
  showDevTools = false,
  renderFooter,
}: RoleArnRowProps) {
  const [forceEditable, setForceEditable] = useState(false);
  const [showRemoveRoleArnModal, setShowRemoveRoleArnModal] = useState(false);
  const [showSkipRoleArnValidationModal, setShowSkipRoleArnValidationModal] = useState(false);

  const formActionsRef = useRef<
    Pick<UseFormReturn<RoleArnInfo>, 'clearErrors' | 'setError' | 'reset'> | null
  >(null);

  const {
    roleArnInfo,
    roleArn,
    isRoleArnLoading,
    refetchRoleArn,
    updateRoleArnMutation,
    deleteRoleArnMutation,
  } = useRoleArnMutations({
    accountId,
    isOrganization,
    onUpdateSuccess: () => {
      formActionsRef.current?.clearErrors('root');
      refetchRoleArn();
      setForceEditable(false);
      setShowSkipRoleArnValidationModal(false);
    },
    onUpdateError: (error: Error) => {
      formActionsRef.current?.setError('root', { message: error.message });
      if (error.message !== 'Internal server error') {
        setShowSkipRoleArnValidationModal(true);
      }
    },
    onDeleteSuccess: () => {
      formActionsRef.current?.reset({ roleArn: '' });
      refetchRoleArn();
      setShowRemoveRoleArnModal(false);
      setForceEditable(false);
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    getValues,
    formState: { isLoading, isSubmitting, errors, isValid, isDirty },
    control,
  } = useForm<RoleArnInfo>({
    defaultValues: { roleArn: '' },
    values: roleArnInfo,
    resetOptions: { keepDirtyValues: true },
  });

  formActionsRef.current = { clearErrors, setError, reset };

  const idPrefix =
    variant === 'SingleAccountView' ? 'role-arn' : `role-arn-${accountId}`;

  const {
    hasError: hasRoleArnError,
    fieldErrorId: roleArnFieldErrorId,
    footerErrorId: roleArnFooterErrorId,
    describedBy: roleArnDescribedBy,
  } = getRoleArnErrorState({
    idPrefix,
    fieldErrorMessage: errors.roleArn?.message,
    hasRootError: Boolean(errors.root),
  });

  const {
    showEditButton,
    showApplyButton,
    isInputDisabled,
    isApplyButtonDisabled,
    isEditButtonDisabled,
    isDeleteButtonDisabled,
  } = getRoleArnInteractionState({
    forceEditable,
    roleArn,
    hasRootError: Boolean(errors.root),
    isFormLoading: isLoading,
    isFormValid: isValid,
    isFormDirty: isDirty,
    isRoleArnLoading,
    isUpdatePending: updateRoleArnMutation.isPending,
    isDeletePending: deleteRoleArnMutation.isPending,
  });

  const onSubmit: SubmitHandler<RoleArnInfo> = (values) => {
    updateRoleArnMutation.mutate(values);
  };

  const applyButtonLoading =
    isLoading || isSubmitting || updateRoleArnMutation.isPending;

  const singleAccountFormRow = (
    <div className="flex gap-[10px]">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 gap-[10px]">
        <Input
          {...register('roleArn', { required: 'Role ARN is required' })}
          aria-invalid={hasRoleArnError}
          aria-describedby={roleArnDescribedBy}
          disabled={isInputDisabled}
          hasError={hasRoleArnError}
          error={errors.roleArn?.message}
          errorId={roleArnFieldErrorId}
          placeholder='Type'
          wrapperClassName="w-full"
          inputClassName="placeholder:text-gray-700!"
        />
        {showApplyButton && (
          <Guard permission="datafy.write.config" mode="disable">
            <div>
              <Button
                type="submit"
                variant="primary"
                disabled={isApplyButtonDisabled}
                className="w-[80px] disabled:opacity-30"
                loading={applyButtonLoading}
              >
                Apply
              </Button>
            </div>
          </Guard>
        )}
      </form>
      {showEditButton && (
        <Guard permission="datafy.write.config" mode="disable">
          <Button
            type="button"
            variant="third"
            disabled={isEditButtonDisabled}
            onClick={() => {
              setForceEditable(true);
              reset(getValues(), {});
            }}
            className="w-[80px] disabled:opacity-30"
          >
            Edit
          </Button>
        </Guard>
      )}
      <Guard permission="datafy.write.config" mode="disable">
        <button
          type="button"
          className="w-10 h-11 grid place-items-center group cursor-pointer disabled:cursor-not-allowed"
          onClick={() => setShowRemoveRoleArnModal(true)}
          disabled={isDeleteButtonDisabled}
          aria-label="Remove IAM role"
        >
          <DeleteIcon className="w-5 h-5 text-gray-900 group-disabled:text-gray-400" />
        </button>
      </Guard>
    </div>
  );

  const organizationAccountRow = (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 gap-[10px]">
      <div className="">
        <Typography
          variant="p3"
          fontWeight={500}
          color="text-primary-900"
          className="y-[18px font-family-inter min-w-[318px] "
        >
          {accountName}
        </Typography>
        <Typography variant="caption" fontWeight={400} color="text-gray-700">
          ID: {accountId}
        </Typography>
      </div>
      <div className="flex flex-col w-full">
        <div className="flex gap-2.5 w-full my-auto">
          <Input
            {...register('roleArn', { required: 'Role ARN is required' })}
            aria-invalid={hasRoleArnError}
            aria-describedby={roleArnDescribedBy}
            disabled={isInputDisabled}
            hasError={hasRoleArnError}
            error={errors.roleArn?.message}
            errorId={roleArnFieldErrorId}
            placeholder={'Type'}
            wrapperClassName="w-full"
            inputClassName="placeholder:text-gray-700!"
          />
          <div className="">
            {showApplyButton && (
              <Guard permission="datafy.write.config" mode="disable">
                <Button
                  type="submit"
                  variant="primary"
                  loading={applyButtonLoading}
                  disabled={isApplyButtonDisabled}
                  className="w-[80px] disabled:opacity-30"
                >
                  Apply
                </Button>
              </Guard>
            )}
            {showEditButton && (
              <Guard permission="datafy.write.config" mode="disable">
                <Button
                  type="button"
                  variant="third"
                  disabled={isEditButtonDisabled}
                  onClick={() => {
                    setForceEditable(true);
                    reset(getValues(), {});
                  }}
                  className="w-[80px] disabled:opacity-30"
                >
                  Edit
                </Button>
              </Guard>
            )}
          </div>
          <Guard permission="datafy.write.config" mode="disable">
            <button
              type="button"
              className="w-10 h-11 grid place-items-center group cursor-pointer disabled:cursor-default"
              onClick={() => setShowRemoveRoleArnModal(true)}
              disabled={isDeleteButtonDisabled}
              aria-label="Remove IAM role"
            >
              <DeleteIcon className="w-5 h-5 text-gray-900 group-disabled:text-gray-400" />
            </button>
          </Guard>
        </div>
        {errors.root && !errors.roleArn && (
          <Typography
            id={roleArnFooterErrorId}
            variant="caption"
            color="text-red"
            className="px-0.5 py-1"
          >
            Input is invalid
          </Typography>
        )}
      </div>
    </form>
  );

  const footerBlock =
    variant === 'SingleAccountView' && renderFooter
      ? renderFooter({
        isLoading,
        isRoleArnLoading,
        isSubmitting,
        isUpdatePending: updateRoleArnMutation.isPending,
        forceEditable,
        roleArn,
        footerErrorId: roleArnFooterErrorId,
        hasRootError: Boolean(errors.root),
      })
      : null;

  return (
    <>
      {variant === 'SingleAccountView' ? (
        <>
          {showDevTools && <DevTool control={control} />}
          <div className="">
            <Typography variant="p3" fontWeight={600} className="text-primary-900 px-0.5 py-1">
              Datafy IAM Role ARN
            </Typography>
            {singleAccountFormRow}
          </div>
          {footerBlock}
        </>
      ) : (
        <div className="flex flex-col gap-[2px] py-5 border-b border-gray-200 ">
          {showDevTools && <DevTool control={control} />}
          {organizationAccountRow}
        </div>
      )}

      <RemoveRoleArnModal
        isOpen={showRemoveRoleArnModal}
        onClose={() => setShowRemoveRoleArnModal(false)}
        onRemove={() => deleteRoleArnMutation.mutate()}
        isLoading={deleteRoleArnMutation.isPending}
        accountName={accountName}
      />

      <SkipRoleArnValidationModal
        isOpen={showSkipRoleArnValidationModal}
        onClose={() => setShowSkipRoleArnValidationModal(false)}
        onSkip={() => {
          updateRoleArnMutation.mutate({
            roleArn: getValues().roleArn,
            skipValidation: true,
          });
        }}
        isLoading={updateRoleArnMutation.isPending}
      />
    </>
  );
}
